import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  Button,
  TextInput,
  Spinner,
  Select,
  Notification,
} from "grommet";
import { Info, Search, Add } from "grommet-icons";
import { useParams } from "react-router-dom";
import adminAxios from "../../config/adminAxios";

import { HeaderNavigation } from "../../components/Headers/AdminHeader";
import { StickyFooter } from "../../components/Footers/GlobalFooter";
import SkillQuestionMappingTable from "../../components/AdminPortal/SkillQuestionMappingTable";
import BreadcrumbNavigation from "../../components/AdminPortal/BreadcrumbNavigation";
import CreateSkillMappingModal from "../../components/AdminPortal/CreateSkillMappingModal";

const SkillQuestionMappingPage = () => {
  const { portfolio_id } = useParams();

  const [skillData, setSkillData] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [skillName, setSkillName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [portfolioName, setPortfolioName] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // Fetch portfolio name
  useEffect(() => {
    if (!portfolio_id) return;
    const fetchPortfolioName = async () => {
      try {
        const { data } = await adminAxios.get(
          `/api/admin/portfolio/${portfolio_id}`,
        );
        setPortfolioName(data.portfolio_name);
      } catch (err) {
        console.error("Error fetching portfolio details:", err);
      }
    };
    fetchPortfolioName();
  }, [portfolio_id]);

  // Fetch mappings and courses
  useEffect(() => {
    if (!portfolio_id) return;
    fetchMappings();
    fetchAllCourses();
  }, [portfolio_id]);

  const fetchMappings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminAxios.get(
        `/api/admin/skill-question-mappings/${portfolio_id}`,
      );
      setSkillData(response.data.skills || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllCourses = async () => {
    try {
      const response = await adminAxios.get(
        `/api/admin/get-all-courses/${portfolio_id}`,
      );
      setAllCourses(response.data.courses || []);
    } catch (err) {
      console.error("Error fetching courses:", err);
    }
  };

  const handleCreateMapping = async () => {
    // your existing toast + refresh logic
    setToastMessage(`New skill added.`);
    setToastVisible(true);
    setShowModal(false);
    setSkillName("");
    fetchMappings();
  };

  const filteredSkills = skillData.filter((item) =>
    item.skillName.toLowerCase().includes(searchValue.toLowerCase()),
  );

  if (loading) {
    return (
      <Box fill align="center" justify="center" background="light-2">
        <Spinner size="large" />
        <Text margin={{ top: "small" }}>
          Loading Skill Question Mappings...
        </Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box fill align="center" justify="center" background="light-2">
        <Text color="status-critical">{error}</Text>
      </Box>
    );
  }

  return (
    <Box fill="vertical" background="light-2">
      {toastVisible && (
        <Notification
          toast
          message={toastMessage}
          status="normal"
          onClose={() => setToastVisible(false)}
        />
      )}

      {/* Header + Breadcrumb */}
      <Box>
        <HeaderNavigation />
        <BreadcrumbNavigation
          breadcrumbs={[
            { label: "Actions", link: `/admin/portfolio/${portfolio_id}` },
            {
              label: "Skill Question Mapping",
              link: `/admin/portfolio/${portfolio_id}/skill-question`,
            },
          ]}
        />
        <Box height="2px" background="brand" margin={{ vertical: "small" }} />
      </Box>

      {/* Content */}
      <Box flex pad="small">
        <Box
          background="#F2F2F2"
          elevation="small"
          round="small"
          pad="medium"
          gap="medium"
        >
          <Box direction="row" align="center" gap="small">
            <Info size="medium" color="brand" />
            <Heading level="3" margin="none" color="brand">
              Skill Question Mapping
            </Heading>
          </Box>
          <Text>
            This page allows you to manage skill-to-question mappings for
            various learning skills. You can view and edit how questions are
            distributed across levels like Beginner and Intermediate, and how
            they&apos;re linked to relevant courses. Use the search bar to find
            skills quickly, or use the action icons to view, add, edit, or
            delete mappings.
          </Text>

          <Box
            direction="row"
            justify="between"
            align="center"
            margin={{ top: "small" }}
          >
            <Box direction="column" gap="xsmall">
              <Heading level="3" margin="none">
                {portfolioName} - Skill Question Mapping
              </Heading>
              <Text>{skillData.length} skills have been added.</Text>
            </Box>
            <Box direction="row" gap="small" align="center">
              <TextInput
                icon={<Search />}
                placeholder="Search skill"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                width="300px"
              />
              <Button
                icon={<Add />}
                label="Create New Skill"
                secondary
                onClick={() => setShowModal(true)}
                style={{ whiteSpace: "nowrap" }}
              />
            </Box>
          </Box>

          <Box round="small" overflow={{ horizontal: "auto" }}>
            <SkillQuestionMappingTable
              skillData={filteredSkills}
              onEdit={(skill) => console.log("Editing skill:", skill)}
              portfolio_id={portfolio_id}
            />
          </Box>
        </Box>
      </Box>

      {/* Use only CreateSkillMappingModal */}
      <CreateSkillMappingModal
        open={showModal}
        onClose={() => setShowModal(false)}
        portfolioId={portfolio_id}
        allCourses={allCourses}
        onCreated={handleCreateMapping}
      />

      <StickyFooter />
    </Box>
  );
};

export default SkillQuestionMappingPage;
